import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { PrepaySelectManager } from '@ctct/application-layer';
import { isStripeBillingStrategy } from '@ctct/domain-layer';
import type { AccountModificationFlowId } from '@ctct/infrastructure-layer';
import type { PrepaySelectHooks, PrepaySelectPageStore } from '@ctct/state-client-layer';
import {
  useBillingStrategy,
  useHasCharmPricing,
  useIsSocialStarter,
  usePaymentPlanFacade,
  useSuspensionInfoFacade,
} from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';
import { resolveBillingStrategyQuery } from '../helpers/resolveStrategyQuery';
import { useIsCoreMutationLockedAccountState } from '../helpers/useIsSingleBillChildAccount';

interface Props {
  initializer: Pick<PrepaySelectManager, 'prepaySelectInit'>;
  pageStore: PrepaySelectPageStore;
  hooks: PrepaySelectHooks;
  flags: Parameters<PrepaySelectManager['prepaySelectInit']>[0]['flags'];
  flowId: AccountModificationFlowId;
}

export const usePrepaySelectWidget = ({ initializer, pageStore, hooks, flags, flowId }: Props) => {
  const { t } = useTranslation();

  const { phase2Enabled: isStripePhase2Enabled } = useBillingStrategy();
  const suspensionInfoQuery = useSuspensionInfoFacade();
  const { isSocialStarter } = useIsSocialStarter();
  const hasCharmPricing = useHasCharmPricing();
  const {
    billingStrategy,
    skipInitializationFromUrl,
    isCustomer,
    isTrialer,
    isSuspended,
    disallowSelectionOfShorterPaymentPlan,
    isMWebVariantEnabled,
    isValueFocusedMWebPlanSelectionEnabled,
    isRemove6MoPrepayEnabled,
  } = flags;

  const shouldWaitForTrialerStatus = Object.prototype.hasOwnProperty.call(flags, 'isTrialer');
  const hasRequiredTrialerStatus = !shouldWaitForTrialerStatus || typeof isTrialer === 'boolean';
  const resolvedBillingStrategy = resolveBillingStrategyQuery(billingStrategy);
  const isStripe = isStripeBillingStrategy(resolvedBillingStrategy);
  const paymentPlan = usePaymentPlanFacade(isStripe, isTrialer ?? false, hasRequiredTrialerStatus);
  const { isLocked: isCoreMutationLockedAccount, isPending: isCoreMutationLockPending } =
    useIsCoreMutationLockedAccountState({ billingStrategy: resolvedBillingStrategy });
  const isConfirmedCoreMutationLockedAccount = Boolean(isCoreMutationLockedAccount && !isCoreMutationLockPending);

  const { lifecycle, ...componentProps } = useMemo(() => {
    const initedManagerReturnValue = initializer.prepaySelectInit({
      pageStore,
      queries: [paymentPlan, suspensionInfoQuery],
      flags: {
        billingStrategy: resolvedBillingStrategy,
        skipInitializationFromUrl,
        isCustomer,
        isSuspended,
        isStripePhase2Enabled: isStripePhase2Enabled ?? false,
        disallowSelectionOfShorterPaymentPlan,
        isSocialStarter,
        isMWebVariantEnabled,
        hasCharmPricing,
        isValueFocusedMWebPlanSelectionEnabled,
        isRemove6MoPrepayEnabled,
        isSingleBillChildAccount: isConfirmedCoreMutationLockedAccount,
        isCoreMutationLockPending: Boolean(isCoreMutationLockPending),
      },
      t,
      flowId,
    });

    return {
      ...initedManagerReturnValue,
      hooks,
      isCustomer,
      isStripe,
      isSingleBillChildAccount: Boolean(isCoreMutationLockedAccount),
    };
  }, [
    initializer,
    pageStore,
    paymentPlan,
    suspensionInfoQuery,
    resolvedBillingStrategy,
    skipInitializationFromUrl,
    isCustomer,
    isTrialer,
    isStripe,
    isStripePhase2Enabled,
    isSuspended,
    disallowSelectionOfShorterPaymentPlan,
    isSocialStarter,
    isMWebVariantEnabled,
    t,
    flowId,
    hooks,
    hasCharmPricing,
    isValueFocusedMWebPlanSelectionEnabled,
    isRemove6MoPrepayEnabled,
    isCoreMutationLockedAccount,
    isCoreMutationLockPending,
    isConfirmedCoreMutationLockedAccount,
  ]);

  useWidgetLifecycle(componentProps.status, lifecycle, isCoreMutationLockPending);

  return componentProps;
};

export type PrepaySelectWidgetProps = ReturnType<typeof usePrepaySelectWidget>;
